import { useCallback, useEffect, useState } from "react";
import { walletRepository } from "../repositories/WalletRepository";
import { PaymentType, WalletType } from "../models/Transactions";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../store";
import {
  resetSearchTransactions,
  storeSearchTransactions,
} from "../store/slices/walletTransactionSearchSlice";

const SEARCH_PAGE_LIMIT = 16;

interface UseWalletTransactionSearchParams {
  walletType: WalletType | null;
  identityPubkey?: string;
}

type SearchSource = 'spark' | 'internal' | 'done';

export function useWalletTransactionSearch({
  walletType,
  identityPubkey,
}: UseWalletTransactionSearchParams) {
  const dispatch = useDispatch<AppDispatch>();
  const searchedTransactions = useSelector((state: RootState) => state.walletTransactionSearch.transactions);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [searchError, setSearchError] = useState('');
  const [sparkSearchOffset, setSparkSearchOffset] = useState(0);
  const [internalSearchOffset, setInternalSearchOffset] = useState(0);
  const [searchSource, setSearchSource] = useState<SearchSource>('spark');
  const [hasMoreSearchResults, setHasMoreSearchResults] = useState(false);

  const trimmedSearchTerm = searchTerm.trim();
  const isSearchActive = isSearchOpen && trimmedSearchTerm.length > 0;
  const isSparkWallet = walletType === WalletType.SPARK;

  const searchSparkPage = useCallback(async (offset: number) => {
    if (!identityPubkey) {
      return [];
    }

    return walletRepository.searchSparkPayments({
      searchTerm: trimmedSearchTerm,
      paymentTypes: [],
      identityPubkey,
      limit: SEARCH_PAGE_LIMIT,
      offset,
    });
  }, [identityPubkey, trimmedSearchTerm]);

  const searchInternalPage = useCallback(async (offset: number) => {
    return walletRepository.searchInternalPayments({
      searchTerm: trimmedSearchTerm,
      paymentTypes: [PaymentType.individual, PaymentType.bulk],
      limit: SEARCH_PAGE_LIMIT,
      offset,
    });
  }, [trimmedSearchTerm]);

  const fetchNextSearchPage = useCallback(async (source: SearchSource) => {
    if (source === 'spark') {
      const sparkResults = await searchSparkPage(sparkSearchOffset);
      if (sparkResults.length > 0) {
        dispatch(storeSearchTransactions(sparkResults));
      }
      setSparkSearchOffset((prev) => prev + sparkResults.length);

      if (sparkResults.length === SEARCH_PAGE_LIMIT) {
        setSearchSource('spark');
        setHasMoreSearchResults(true);
        return;
      }
    }

    const internalResults = await searchInternalPage(internalSearchOffset);
    if (internalResults.length > 0) {
      dispatch(storeSearchTransactions(internalResults));
    }
    setInternalSearchOffset((prev) => prev + internalResults.length);

    if (internalResults.length === SEARCH_PAGE_LIMIT) {
      setSearchSource('internal');
      setHasMoreSearchResults(true);
      return;
    }

    setSearchSource('done');
    setHasMoreSearchResults(false);
  }, [searchSparkPage, sparkSearchOffset, searchInternalPage, internalSearchOffset, dispatch]);

  useEffect(() => {
    if (!isSearchOpen) {
      setSearchTerm('');
      setSearchError('');
      dispatch(resetSearchTransactions());
      setIsSearching(false);
      setSparkSearchOffset(0);
      setInternalSearchOffset(0);
      setSearchSource('spark');
      setHasMoreSearchResults(false);
      return;
    }

    if (trimmedSearchTerm.length === 0) {
      setSearchError('');
      dispatch(resetSearchTransactions());
      setIsSearching(false);
      setSparkSearchOffset(0);
      setInternalSearchOffset(0);
      setSearchSource('spark');
      setHasMoreSearchResults(false);
      return;
    }

    if (!isSparkWallet || !identityPubkey) {
      setSearchError('Search is available for Spark wallet only');
      dispatch(resetSearchTransactions());
      setIsSearching(false);
      setSparkSearchOffset(0);
      setInternalSearchOffset(0);
      setSearchSource('spark');
      setHasMoreSearchResults(false);
      return;
    }

    let isCancelled = false;
    const timeoutId = setTimeout(async () => {
      try {
        setIsSearching(true);
        setSearchError('');
        dispatch(resetSearchTransactions());
        setSparkSearchOffset(0);
        setInternalSearchOffset(0);
        setSearchSource('spark');
        setHasMoreSearchResults(false);

        const initialSparkResults = await searchSparkPage(0);
        if (isCancelled) {
          return;
        }

        if (initialSparkResults.length > 0) {
          dispatch(storeSearchTransactions(initialSparkResults));
        }
        setSparkSearchOffset(initialSparkResults.length);

        if (initialSparkResults.length === SEARCH_PAGE_LIMIT) {
          setSearchSource('spark');
          setHasMoreSearchResults(true);
          return;
        }

        const initialInternalResults = await searchInternalPage(0);
        if (isCancelled) {
          return;
        }

        if (initialInternalResults.length > 0) {
          dispatch(storeSearchTransactions(initialInternalResults));
        }
        setInternalSearchOffset(initialInternalResults.length);
        if (initialInternalResults.length === SEARCH_PAGE_LIMIT) {
          setSearchSource('internal');
          setHasMoreSearchResults(true);
          return;
        }

        setSearchSource('done');
        setHasMoreSearchResults(false);
      } catch (_err) {
        if (!isCancelled) {
          setSearchError('Error while searching transactions');
          dispatch(resetSearchTransactions());
          setSparkSearchOffset(0);
          setInternalSearchOffset(0);
          setSearchSource('spark');
          setHasMoreSearchResults(false);
        }
      } finally {
        if (!isCancelled) {
          setIsSearching(false);
        }
      }
    }, 300);

    return () => {
      isCancelled = true;
      clearTimeout(timeoutId);
    };
  }, [isSearchOpen, trimmedSearchTerm, isSparkWallet, identityPubkey, searchSparkPage, searchInternalPage, dispatch]);

  const loadMoreSearchResults = async () => {
    if (!isSearchActive || isSearching || !hasMoreSearchResults) {
      return;
    }
    if (!isSparkWallet || !identityPubkey) {
      return;
    }

    try {
      setIsSearching(true);
      setSearchError('');
      await fetchNextSearchPage(searchSource);
    } catch (_err) {
      setSearchError('Error while searching transactions');
    } finally {
      setIsSearching(false);
    }
  };

  return {
    isSparkWallet,
    isSearchOpen,
    setIsSearchOpen,
    searchTerm,
    setSearchTerm,
    isSearchActive,
    searchedTransactions,
    isSearching,
    searchError,
    hasMoreSearchResults,
    loadMoreSearchResults,
  };
}
